import { getScrollableParent } from '@components/DropdownContext/dropdownUtils';
import { useCloseContextMenuOnScroll } from '@hooks/useCloseContextMenuOnScroll';
import type {
    ButtonPropSize,
    ButtonPropView,
    ContextMenuGroupDefault,
    ContextMenuPropGetItemAttributes,
    ContextMenuProps,
    IconComponent,
    IconProps,
} from 'uikit';
import { Button, ContextMenu, IconKebab } from 'uikit';

import type React from 'react';
import type { FC, MouseEventHandler } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type ContextMenuPropsList = ContextMenuProps<typeof ContextMenu>;

export interface ItemAction {
    label: string;
    action: (itemId?: number, context?: any, item?: any) => void;
    itemId?: number;
    groupId?: number;
    disabled?: boolean;
    menu?: ItemAction[];
}

export type IDropdownContextProps = Omit<ContextMenuProps<ItemAction, ContextMenuGroupDefault>, 'getLabel'> & {
    items?: ItemAction[];
    isDisabled?: boolean;
    icon?: IconComponent;
    wrapperClassName?: string;
    direction?: ContextMenuPropsList['direction'];
    offset?: ContextMenuPropsList['offset'];
    btnView?: ButtonPropView;
    btnSize?: ButtonPropSize;
    btnIconSize?: IconProps['size'];
    leftSide?: React.ReactNode;
    groups?: ContextMenuGroupDefault[];
    btnLabel?: string;
    getItemAttributesHandler?: ContextMenuPropGetItemAttributes<any>;
    onMouseMoveHandler?: MouseEventHandler<HTMLDivElement>;
    onMouseLeaveHandler?: MouseEventHandler<HTMLDivElement>;
    context?: any;
    isDropdownWidthMatchBtn?: boolean;
    isCommit?: boolean;
};

export const DropdownContext: FC<IDropdownContextProps> = ({
    items = [],
    isDisabled = false,
    icon = IconKebab,
    wrapperClassName,
    direction = 'downStartLeft',
    offset = 8,
    btnView = 'clear',
    btnSize = 'xs',
    btnIconSize = 's',
    leftSide,
    groups,
    btnLabel = '',
    getItemAttributesHandler,
    onMouseMoveHandler,
    onMouseLeaveHandler,
    context,
    getItemDisabled,
    getItemLabel,
    getItemOnClick,
    getItemLeftSide,
    className,
    isDropdownWidthMatchBtn = false,
    isCommit = false,
}) => {
    const [isContextMenuOpen, setIsContextMenuOpen] = useState<boolean>(false);
    const ref = useRef<HTMLDivElement>(null);
    const [scrollableParent, setScrollableParent] = useState<HTMLElement | null>(null);

    useCloseContextMenuOnScroll({ setOpen: setIsContextMenuOpen });

    useEffect(() => {
        setScrollableParent(getScrollableParent(ref.current?.parentElement ?? null));
    }, [ref.current?.parentElement]);

    const wrapperWidth = ref?.current?.getBoundingClientRect().width;

    const handleToggle = useCallback(() => {
        setIsContextMenuOpen((state) => !state);
    }, []);

    const handleStopPropagation = useCallback((evt) => {
        evt.stopPropagation();
    }, []);

    const modifyItems = useMemo(
        () =>
            items.map((item) => ({
                ...item,
                action: () => {
                    item.action?.(item?.itemId, context, item);
                    handleToggle();
                },
            })),
        [context, handleToggle, items],
    );

    const dropdownWidthStyle = useMemo(() => {
        if (isDropdownWidthMatchBtn) {
            return {
                width: wrapperWidth,
            };
        }

        return undefined;
    }, [isDropdownWidthMatchBtn, wrapperWidth]);

    useEffect(() => {
        if (!scrollableParent || !isContextMenuOpen) {
            return () => {};
        }

        const listener = () => {
            handleToggle();
            scrollableParent.removeEventListener('scroll', listener);
        };

        scrollableParent.addEventListener('scroll', listener);

        return () => {
            scrollableParent.removeEventListener('scroll', listener);
        };
    }, [scrollableParent, isContextMenuOpen, handleToggle]);

    return (
        <div ref={ref} className={wrapperClassName ?? ''} onClick={handleStopPropagation} role="none">
            {leftSide}
            <Button
                view={btnView}
                iconRight={icon}
                iconSize={btnIconSize}
                size={btnSize}
                disabled={isDisabled}
                onClick={handleToggle}
                label={btnLabel}
                loading={isCommit}
            />
            <ContextMenu
                anchorRef={ref}
                isOpen={isContextMenuOpen}
                items={modifyItems}
                direction={direction}
                offset={offset}
                getItemLabel={getItemLabel || (({ label }) => label)}
                getItemGroupId={({ groupId }) => groupId}
                getItemDisabled={getItemDisabled || (({ disabled }) => disabled)}
                getItemOnClick={getItemOnClick || (({ action }) => action)}
                getItemLeftSide={getItemLeftSide}
                groups={groups}
                getGroupId={({ id }) => id}
                onClickOutside={handleToggle}
                getItemAttributes={getItemAttributesHandler}
                onMouseMove={onMouseMoveHandler}
                onMouseLeave={onMouseLeaveHandler}
                size="s"
                className={className}
                style={dropdownWidthStyle}
                getItemSubMenu={({ menu }) => menu}
            />
        </div>
    );
};

DropdownContext.displayName = 'DropdownContext';
